import api from './apiClient';

// Service for file operations
const fileService = {
  /**
   * Get file by ID
   * @param {string} fileId - The file ID
   * @returns {Promise<Object>} File metadata with URL
   */
  async getFileById(fileId) {
    return await api.get(`/files/${encodeURIComponent(fileId)}`);
  },

  /**
   * Get file download URL (direct file content)
   * @param {string} fileId - The file ID
   * @returns {string} Direct file download URL
   */
  getFileDownloadUrl(fileId) {
    const apiBase = api.getBaseUrl();
    return `${apiBase}/files/${encodeURIComponent(fileId)}/download`;
  },

  /**
   * Get file content URL (direct file content)
   * @param {string} fileId - The file ID
   * @returns {string} Direct file content URL
   */
  getFileContentUrl(fileId) {
    const apiBase = api.getBaseUrl();
    return `${apiBase}/files/${encodeURIComponent(fileId)}/content`;
  },

  /**
   * Resolve file ID reference in text
   * Supports formats like: "file:file_123456" or "{{file:file_123456}}"
   * @param {string} text - Text that may contain file ID references
   * @returns {Promise<string>} Text with file IDs replaced with URLs
   */
  async resolveFileReferences(text) {
    if (!text || typeof text !== 'string') return text;

    // Match patterns like: file:file_123456 or {{file:file_123456}}
    const fileIdPattern = /(?:{{)?file:([a-zA-Z0-9_\-]+)(?:}})?/g;
    const matches = [...text.matchAll(fileIdPattern)];
    
    if (matches.length === 0) return text;

    // Get all unique file IDs
    const fileIds = [...new Set(matches.map(m => m[1]))];
    
    // Fetch all files in parallel
    const filePromises = fileIds.map(async (fileId) => {
      try {
        const file = await this.getFileById(fileId);
        return { fileId, url: file?.url || null };
      } catch (error) {
        console.error(`Failed to get file ${fileId}:`, error);
        return { fileId, url: null };
      }
    });

    const fileResults = await Promise.all(filePromises);
    const fileMap = new Map(fileResults.map(r => [r.fileId, r.url]));

    // Replace all matches with URLs
    let resolvedText = text;
    matches.forEach(match => {
      const fileId = match[1];
      const url = fileMap.get(fileId);
      if (url) {
        // Replace the match with an <a> tag or just the URL
        resolvedText = resolvedText.replace(match[0], url);
      }
    });

    return resolvedText;
  },
};

export default fileService;

